import { FC, useContext, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { isLeft } from "fp-ts/lib/Either";
import { ErrorMessage } from "@hookform/error-message";
import LocalizationContextType from "../../../../../domain/providers/localization/LocalizationContextType";
import ModalsContextType from "../../../../../domain/providers/modal/ModalsContextType";
import LocalizationContext from "../../../../providers/localization/LocalizationContext";
import ModalsContext from "../../../../providers/modal/ModalsContext";
import ReasignShiftModalProps from "./reasign_shift_modal.props";
import di from "../../../../../DependencyInjection";
import ButtonComponent from "../../../../components/button/ButtonComponent";
import GetAvailableUsersUseCase, { GetAvailableUsersUseCaseName } from "../../../../../domain/use_cases/user/GetAvailableUsersUseCase";
import UserEntity from "../../../../../domain/entities/UserEntity";
import ReasignShiftUseCase, { ReasignShiftUseCaseName } from "../../../../../domain/use_cases/shift/ReasignShiftUseCase";
import Validators from "../../../../utils/Validators";
import AutoCompleteWithCheckComponent from "../../../../components/autocompleteWithCheck/AutoCompleteWithCheckComponent";
import { AutoCompleteItem } from "../../../../components/autocompleteWithCheck/AutoCompleteWithCheckProps";
import KeyWordLocalization from "../../../../utils/KeyWordLocalization";

const ReasignShiftModal: FC<ReasignShiftModalProps> = ({
    shift,
    onReasign,
    onClose,
}) => {
    const {
        handleSubmit,
        formState: { errors },
    } = useForm();
    const formInfo = useForm();
    const { i18n } = useContext(LocalizationContext) as LocalizationContextType;
    const { addToast } = useContext(ModalsContext) as ModalsContextType;
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<UserEntity | null>(null);

    // Función para buscar usuarios disponibles usando GetAvailableUsersUseCase
    const _onSearchUsers = async (query: string): Promise<AutoCompleteItem<UserEntity>[]> => {
        if (!shift || !shift.schedule) {
            return [];
        }

        const dateWithoutTime = new Date(shift.startDate);
        dateWithoutTime.setHours(0, 0, 0, 0);
        console.log("fecha sin tiempo:", dateWithoutTime);

        const response = await di.get<GetAvailableUsersUseCase>(GetAvailableUsersUseCaseName).call({
            query: query,
            scheduleId: shift.scheduleId ?? shift.schedule.id,
            date: dateWithoutTime,
        });

        if (isLeft(response)) {
            return [];
        }

        return response.right.map((user) => ({
            label: `${user.name} - ${user.email || 'Sin email'}`,
            id: user.id,
            aditionalValue: user,
        }));
    };

    // Función para manejar el cambio de usuario seleccionado
    const handleChangeUser = (item: AutoCompleteItem<UserEntity>) => {
        setSelectedUser(item ? item.aditionalValue : null);
    };

    // Función para manejar la reasignación
    const _handleReasign = async (data: any) => {
        if (!shift || !selectedUser) {
            addToast(i18n("Debe seleccionar un usuario"), "error", undefined);
            return;
        }

        setLoading(true);

        try {
            const response = await di.get<ReasignShiftUseCase>(ReasignShiftUseCaseName)
                .call({
                    shiftId: shift.turnId,
                    newUserId: selectedUser.id,
                    oldUserId: shift.user.id,
                    reason: data.reason,
                });

            if (isLeft(response)) {
                addToast(i18n("Error al reasignar turno"), "error", undefined);
            } else {
                addToast(i18n("Turno reasignado exitosamente"), "success", undefined);
                onReasign(response.right);
                onClose();
            }
        } catch (error) {
            console.error("Error reasignando turno:", error);
            addToast(i18n("Error al reasignar turno"), "error", undefined);
        }

        setLoading(false);
    };

    if (!shift) return null;

    return (
        <form onSubmit={handleSubmit(_handleReasign)}>
            {/* Información del turno actual */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card bg-light">
                        <div className="card-header">
                            <h6 className="mb-0">{i18n("Información del Turno")}</h6>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-6">
                                    <p><strong>{i18n("Establecimiento")}:</strong> {shift.stablishment.name}</p>
                                    <p><strong>{i18n("Fecha")}:</strong> {shift.startDate.toLocaleDateString()}</p>
                                </div>
                                <div className="col-md-6">
                                    <p><strong>{i18n("Usuario Actual")}:</strong> {shift.user.name}</p>
                                    <p><strong>{i18n("Horario")}:</strong>
                                        {shift.schedule ?
                                            ` ${shift.startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${shift.endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                                            : i18n("No definido")
                                        }
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Selector de nuevo usuario con AutoComplete */}
            <div className="row">
                <div className="col-12">
                    <div className="form-group">
                        <AutoCompleteWithCheckComponent
                            keyName={KeyWordLocalization.ReasignShiftModalUser}
                            label={i18n("Nuevo Usuario para Asignar")}
                            formInfo={formInfo}
                            multiple={false}
                            onSearch={_onSearchUsers}
                            onChange={handleChangeUser}
                        />
                        <small className="text-muted">
                            {i18n("Busque y seleccione el usuario disponible para este turno")}
                        </small>
                    </div>
                </div>
            </div>

            {/* Resumen de la reasignación */}
            {selectedUser && (
                <div className="row mt-4">
                    <div className="col-12">
                        <div className="alert alert-info">
                            <h6 className="alert-heading">
                                <span className="material-symbols-outlined me-1">info</span>
                                {i18n("Resumen de Reasignación")}
                            </h6>
                            <p className="mb-1">
                                <strong>{i18n("De")}:</strong> {shift.user.name} ({shift.user.email || 'Sin email'})
                            </p>
                            <p className="mb-0">
                                <strong>{i18n("Para")}:</strong> {selectedUser.name} ({selectedUser.email || 'Sin email'})
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Botones de acción */}
            <div className="row mt-4">
                <div className="col-12 d-flex justify-content-end gap-2">
                    <ButtonComponent
                        onClick={onClose}
                        design="secondary"
                        text={i18n("Cancelar")}
                        disabled={loading}
                    />
                    <ButtonComponent
                        isLoading={loading}
                        icon={<span className="material-symbols-outlined">swap_horiz</span>}
                        type="submit"
                        design="primary"
                        text={i18n("Reasignar Turno")}
                        disabled={!selectedUser || loading}
                    />
                </div>
            </div>
        </form>
    );
};

export default ReasignShiftModal;